import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FilterMenu from './FilterMenu';
import SearchPanel from './SearchPanel';
import './Header.css';

// Icons
const SearchIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);





const Header = ({ onOpenAuth, onLogout, onFilterChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFavoritesListMode, setIsFavoritesListMode] = useState(false);
  const [isProfileListMode, setIsProfileListMode] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setSearchOpen(false);
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Check if favourites page is in list mode
  useEffect(() => {
    const isFavoritesPage = location.pathname === '/favorites';
    if (isFavoritesPage) {
      let isListMode = false;
      const checkListMode = () => {
        isListMode = true;
      };
      window.addEventListener('profileListModeResponse', checkListMode);
      window.dispatchEvent(new CustomEvent('checkProfileListMode'));
      
      setTimeout(() => {
        window.removeEventListener('profileListModeResponse', checkListMode);
        setIsFavoritesListMode(isListMode);
      }, 50);
    } else {
      setIsFavoritesListMode(false);
    }
  }, [location.pathname]);

  // Listen for list mode changes on favourites page
  useEffect(() => {
    const handleFavouritesListModeChange = (e) => {
      if (location.pathname === '/favorites') {
        setIsFavoritesListMode(e.detail.listMode);
      }
    };
    
    window.addEventListener('favouritesListModeChange', handleFavouritesListModeChange);
    
    return () => {
      window.removeEventListener('favouritesListModeChange', handleFavouritesListModeChange);
    };
  }, [location.pathname]);

  // Check if profile/account page is in list mode
  useEffect(() => {
    const isProfileOrAccountPage = location.pathname === '/profile' || location.pathname === '/@tearoom' || location.pathname === '/account';
    if (isProfileOrAccountPage) {
      let isListMode = false;
      const checkListMode = () => {
        isListMode = true;
      };
      window.addEventListener('profileListModeResponse', checkListMode);
      window.dispatchEvent(new CustomEvent('checkProfileListMode'));
      
      setTimeout(() => {
        window.removeEventListener('profileListModeResponse', checkListMode);
        setIsProfileListMode(isListMode);
      }, 50);
    } else {
      setIsProfileListMode(false);
    }
  }, [location.pathname]);

  // Listen for profile list mode changes
  useEffect(() => {
    const handleProfileListModeChange = (e) => {
      const isProfileOrAccountPage = location.pathname === '/profile' || location.pathname === '/@tearoom' || location.pathname === '/account';
      if (isProfileOrAccountPage) {
        setIsProfileListMode(e.detail?.listMode || false);
      }
    };
    
    window.addEventListener('profileListModeChange', handleProfileListModeChange);
    
    return () => {
      window.removeEventListener('profileListModeChange', handleProfileListModeChange);
    };
  }, [location.pathname]);

  const handleSearchToggle = useCallback(() => {
    setSearchOpen(prev => !prev);
    setIsMenuOpen(false);
  }, []);

  const handleMenuToggle = useCallback(() => {
    setIsMenuOpen(prev => !prev);
    setSearchOpen(false);
  }, []);

  const handleBack = useCallback(() => {
    const isProfilePage = location.pathname === '/profile' || location.pathname === '/@tearoom' || location.pathname === '/account';
    const isFavoritesPage = location.pathname === '/favorites';
    const isProductPage = location.pathname.startsWith('/product/') || location.pathname.startsWith('/products/');
    
    if (isProductPage) {
      // Check query parameter for 'from'
      const searchParams = new URLSearchParams(location.search);
      const from = searchParams.get('from');
      
      if (from === 'orders') {
        navigate('/orders');
      } else if (from === 'cart') {
        navigate('/cart');
      } else if (from === 'search') {
        navigate(-1); // back to the results page, with its query
      } else {
        // Check if we came from profile via location state
        const fromProfile = location.state?.fromProfile;
        if (fromProfile) {
          navigate(fromProfile); // Navigate back to profile
        } else {
          navigate('/'); // Navigate to products
        }
      }
    } else if (isProfilePage || isFavoritesPage) {
      // Check if page is in list mode by checking a custom event
      let isListMode = false;
      const checkListMode = () => {
        isListMode = true;
      };
      window.addEventListener('profileListModeResponse', checkListMode);
      window.dispatchEvent(new CustomEvent('checkProfileListMode'));
      
      // Wait briefly for response, then navigate
      setTimeout(() => {
        window.removeEventListener('profileListModeResponse', checkListMode);
        if (isListMode) {
          // In list mode - trigger exit list mode by navigating to grid
          window.dispatchEvent(new CustomEvent('exitProfileListMode'));
        } else {
          // In grid mode - go to products
          navigate('/');
        }
      }, 50);
    } else {
      navigate('/');
    }
  }, [navigate, location.pathname, location.search, location.state]);

  // Determine which button to show
  const isFavoritesPage = location.pathname === '/favorites';
  const isProductPage = location.pathname.startsWith('/product/') || location.pathname.startsWith('/products/');
  const isAccountPage = location.pathname === '/account';
  const showMenuButton = false; // Never show menu button
  // Show back button only when in list mode on profile/account pages, on product pages, or when in list mode on favorites
  // Don't show back button when on profile/account page in grid mode (initial view)
  const showBackButton = isProductPage || (isFavoritesPage && isFavoritesListMode) || isProfileListMode;
  const showLogoutButton = isAccountPage && !showBackButton;


  // "show all results" (Enter in the search panel): the full results page
  const handleSearchSubmit = useCallback((query) => {
    const q = (query || '').trim();
    if (q !== '') {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      setSearchOpen(false);
    }
  }, [navigate]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Escape') {
      setSearchOpen(false);
      setIsMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);


  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''} ${location.pathname === '/' ? 'header--overlay' : ''} ${searchOpen ? 'header--search-open' : ''}`}>
      <div className="header__container">
        <div className="header__content">
          {/* Left section */}
          <div className="header__left">
            {showBackButton && (
              <button 
                className="header__button header__button--back"
                onClick={handleBack}
                aria-label="Back"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {showLogoutButton && onLogout && (
              <button 
                className="header__button header__button--logout"
                onClick={onLogout}
                aria-label="Logout"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m7 14l5-5-5-5m5 5H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {showMenuButton && (
              <button 
                className="header__button header__button--menu"
                onClick={handleMenuToggle}
                aria-label="Menu"
                aria-expanded={isMenuOpen}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>

          {/* Center section */}
          <div className="header__center">
            <h1 className="header__logo" onClick={() => navigate('/')}>
              {window.location.hostname.split('.')[0].toLowerCase() === 'sexymood' ? 'SexyMood' : 'ruben'}
            </h1>
          </div>

          {/* Right section */}
          <div className="header__right">
            <button 
              className="header__button header__button--search"
              onClick={handleSearchToggle}
              aria-label="Search"
              aria-expanded={searchOpen}
            >
              <SearchIcon />
            </button>
          </div>
        </div>

        {/* Search: pill input + dropdown with shops and products */}
        <SearchPanel
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSubmitAll={handleSearchSubmit}
        />
      </div>

      {/* Filter Menu - only show if menu button is visible */}
      {showMenuButton && (
        <FilterMenu 
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          onFilterChange={onFilterChange}
        />
      )}
    </header>
  );
};

export default Header;
